import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { SampleBadge } from "@/components/ImageLibrary";

export const photoList = (item) => {
  const imgs = (item?.images || []).filter(Boolean);
  return imgs.length ? imgs : item?.image ? [item.image] : [];
};

const SWIPE = 40;
const MAX_ZOOM = 4;
const NO_ZOOM = { s: 1, x: 0, y: 0 };
const arrowCls = "absolute top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white z-10";
const ctrlCls = "w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";
const clampZ = (s) => Math.min(MAX_ZOOM, Math.max(1, s));

// Locks page scroll (incl. iOS Safari) by pinning <body>; restores the exact scroll position on unlock.
function lockScroll() {
  const y = window.scrollY;
  const b = document.body.style;
  const saved = { position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow };
  const html = document.documentElement.style;
  const savedOb = html.overscrollBehavior;
  Object.assign(b, { position: "fixed", top: `-${y}px`, left: "0", right: "0", width: "100%", overflow: "hidden" });
  html.overscrollBehavior = "none";
  return () => {
    Object.assign(b, saved);
    html.overscrollBehavior = savedOb;
    window.scrollTo({ top: y, left: 0, behavior: "instant" });
  };
}

// Pinch / pan / double-tap zoom on touch, +/−/Reset buttons on desktop. Swipe navigates only when not zoomed.
function useZoom(stageRef, imgRef, onSwipe) {
  const [z, setZ] = useState(NO_ZOOM);
  const [live, setLive] = useState(false);
  const zr = useRef(NO_ZOOM);
  const g = useRef(null);
  const lastTap = useRef(0);
  const apply = (next) => {
    const img = imgRef.current;
    const bx = img ? (img.offsetWidth * (next.s - 1)) / 2 : 0;
    const by = img ? (img.offsetHeight * (next.s - 1)) / 2 : 0;
    const v = next.s <= 1 ? NO_ZOOM : { s: next.s, x: Math.max(-bx, Math.min(bx, next.x)), y: Math.max(-by, Math.min(by, next.y)) };
    zr.current = v;
    setZ(v);
  };
  const zoomBy = (d) => { const s = clampZ(zr.current.s + d); apply({ s, x: zr.current.x * (s / zr.current.s), y: zr.current.y * (s / zr.current.s) }); };
  const reset = () => apply(NO_ZOOM);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const start = (e) => {
      const t = e.touches;
      if (t.length === 1 && !g.current) g.current = { multi: false, sx: t[0].clientX, sy: t[0].clientY, z0: zr.current };
      if (t.length === 2) {
        g.current = { ...(g.current || {}), multi: true, d0: dist(t) || 1, z0: zr.current };
      }
      if (t.length === 1 && g.current) Object.assign(g.current, { px: t[0].clientX, py: t[0].clientY, pz: zr.current });
      setLive(true);
    };
    const move = (e) => {
      e.preventDefault();
      const t = e.touches;
      const st = g.current;
      if (!st) return;
      if (t.length === 2 && st.d0) {
        const s = clampZ(st.z0.s * (dist(t) / st.d0));
        apply({ s, x: st.z0.x * (s / st.z0.s), y: st.z0.y * (s / st.z0.s) });
      } else if (t.length === 1 && zr.current.s > 1 && st.px != null) {
        apply({ s: zr.current.s, x: st.pz.x + (t[0].clientX - st.px), y: st.pz.y + (t[0].clientY - st.py) });
      }
    };
    const end = (e) => {
      const st = g.current;
      if (e.touches.length === 1 && st) Object.assign(st, { px: e.touches[0].clientX, py: e.touches[0].clientY, pz: zr.current, d0: null });
      if (e.touches.length > 0 || !st) return;
      g.current = null;
      setLive(false);
      const c = e.changedTouches[0];
      if (!c) return;
      const dx = c.clientX - st.sx;
      const dy = c.clientY - st.sy;
      if (zr.current.s < 1.05) apply(NO_ZOOM);
      if (st.multi) return;
      if (!st.multi && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        const now = Date.now();
        if (now - lastTap.current < 300) { apply(zr.current.s > 1 ? NO_ZOOM : { s: 2, x: 0, y: 0 }); lastTap.current = 0; } else lastTap.current = now;
        return;
      }
      if (st.z0.s > 1) return;
      if (Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy)) onSwipe(dx < 0 ? 1 : -1);
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mouse drag to pan while zoomed (desktop).
  const onMouseDown = (e) => {
    if (zr.current.s <= 1) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, z0 = zr.current;
    setLive(true);
    const mv = (ev) => apply({ s: z0.s, x: z0.x + ev.clientX - sx, y: z0.y + ev.clientY - sy });
    const up = () => { setLive(false); window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
  };
  return { z, live, zoomBy, reset, onMouseDown };
}

// Full-screen viewer: black backdrop, swipe/arrows/keyboard, pinch & button zoom, caption, X/Esc to close. No autoplay.
function FullscreenViewer({ list, captions, start, alt, onClose, testid }) {
  const [i, setI] = useState(start);
  const closeRef = useRef(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const n = list.length;
  const go = (d) => setI((x) => (x + d + n) % n);
  const zoom = useZoom(stageRef, imgRef, (d) => { if (n > 1) go(d); });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => { zoom.reset(); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unlock = lockScroll();
    closeRef.current?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && n > 1) go(1);
      else if (e.key === "ArrowLeft" && n > 1) go(-1);
      else if (e.key === "+" || e.key === "=") zoomRef.current.zoomBy(0.5);
      else if (e.key === "-") zoomRef.current.zoomBy(-0.5);
      else if (e.key === "0") zoomRef.current.reset();
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); unlock(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cap = (captions || {})[list[i]];
  const { s, x, y } = zoom.z;
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Photo viewer" className="fixed inset-0 z-[200] bg-black flex flex-col overscroll-none"
      style={{ touchAction: "none" }}
      onClick={stop} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onMouseDown={stop}
      data-testid={`${testid}-fullscreen`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 text-white/80 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span data-testid={`${testid}-fullscreen-counter`}>{n > 1 ? `${i + 1} / ${n}` : ""}</span>
          <SampleBadge url={list[i]} testid={`${testid}-fullscreen-sample`} />
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2" data-testid={`${testid}-fullscreen-zoom-controls`}>
            <button type="button" onClick={() => zoom.zoomBy(-0.5)} disabled={s <= 1} aria-label="Zoom out" className={ctrlCls} data-testid={`${testid}-fullscreen-zoom-out`}><ZoomOut className="w-5 h-5" /></button>
            <span className="w-12 text-center tabular-nums text-xs" data-testid={`${testid}-fullscreen-zoom-level`}>{Math.round(s * 100)}%</span>
            <button type="button" onClick={() => zoom.zoomBy(0.5)} disabled={s >= MAX_ZOOM} aria-label="Zoom in" className={ctrlCls} data-testid={`${testid}-fullscreen-zoom-in`}><ZoomIn className="w-5 h-5" /></button>
            <button type="button" onClick={zoom.reset} disabled={s <= 1} aria-label="Reset zoom" className={ctrlCls} data-testid={`${testid}-fullscreen-zoom-reset`}><RotateCcw className="w-4 h-4" /></button>
          </div>
          {s > 1 && <button type="button" onClick={zoom.reset} className="sm:hidden h-10 px-3 rounded-full bg-white/10 text-white text-xs font-semibold" data-testid={`${testid}-fullscreen-zoom-reset-mobile`}>Reset</button>}
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className={ctrlCls} data-testid={`${testid}-fullscreen-close`}><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div ref={stageRef} onMouseDown={zoom.onMouseDown} className={`relative flex-1 min-h-0 flex items-center justify-center overflow-hidden ${s > 1 ? "cursor-grab active:cursor-grabbing" : ""}`} data-testid={`${testid}-fullscreen-stage`}>
        <img ref={imgRef} src={resolveImageUrl(list[i])} alt={cap || alt} draggable={false}
          className={`max-w-full max-h-full object-contain select-none ${zoom.live ? "" : "transition-transform duration-200"}`}
          style={{ transform: `translate3d(${x}px, ${y}px, 0) scale(${s})` }}
          data-testid={`${testid}-fullscreen-img`} data-zoom={s} />
        {n > 1 && <>
          <button type="button" onClick={() => go(-1)} aria-label="Previous photo" className={`${arrowCls} left-3 w-11 h-11 bg-white/10 hover:bg-white/25`} data-testid={`${testid}-fullscreen-prev`}><ChevronLeft className="w-6 h-6" /></button>
          <button type="button" onClick={() => go(1)} aria-label="Next photo" className={`${arrowCls} right-3 w-11 h-11 bg-white/10 hover:bg-white/25`} data-testid={`${testid}-fullscreen-next`}><ChevronRight className="w-6 h-6" /></button>
        </>}
      </div>
      <div className="min-h-[3rem] px-4 py-3 text-center text-sm text-white/90 break-words" data-testid={`${testid}-fullscreen-caption`}>{cap || ""}</div>
    </div>,
    document.body,
  );
}

// Horizontal auto-advancing photo slider: 4s autoplay, swipe, desktop arrows, pause on interaction, tap = full screen.
// badgeClass: when set, shows the "Sample image" label for the current photo in that top corner (kept clear of the caption strip).
export default function ImageSlider({ images = [], captions = null, alt = "", className = "h-64", imgClassName = "object-cover", interval = 4000, testid = "image-slider", fullscreen = true, overlayBottom = "", badgeClass = "", children }) {
  const list = images.filter(Boolean);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [viewer, setViewer] = useState(false);
  const touch = useRef(null);
  const swiped = useRef(false);
  const rootRef = useRef(null);
  const resume = useRef(null);
  const hover = useRef(false);
  const n = list.length;
  const go = (d) => setI((x) => (x + d + n) % n);

  useEffect(() => () => clearTimeout(resume.current), []);
  useEffect(() => { if (i >= n) setI(0); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (n < 2 || paused || viewer) return;
    const t = setInterval(() => { if (!document.hidden) setI((x) => (x + 1) % n); }, interval);
    return () => clearInterval(t);
  }, [n, paused, viewer, interval]);

  if (!n) return null;
  const cap = (captions || {})[list[i]];
  const closeViewer = () => {
    setViewer(false);
    rootRef.current?.focus({ preventScroll: true });
    hover.current = !!rootRef.current?.matches(":hover");
    setPaused(hover.current);
  };
  return (
    <div
      ref={rootRef} tabIndex={-1}
      className={`relative overflow-hidden select-none outline-none ${className}`}
      onMouseEnter={() => { hover.current = true; setPaused(true); }} onMouseLeave={() => { hover.current = false; setPaused(false); }}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(hover.current)}
      onTouchStart={(e) => { setPaused(true); swiped.current = false; touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
        if (Math.abs(dx) > SWIPE) { swiped.current = true; go(dx < 0 ? 1 : -1); }
        clearTimeout(resume.current); resume.current = setTimeout(() => setPaused(false), 6000);
      }}
      data-testid={testid}
    >
      <div className="flex h-full transition-transform duration-500 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
        {list.map((src, k) => (
          <img key={`${src}-${k}`} src={resolveImageUrl(src)} alt={(captions || {})[src] || (n > 1 ? `${alt} ${k + 1}/${n}` : alt)} loading={k === 0 ? "eager" : "lazy"} draggable={false}
            onClick={() => { if (fullscreen && !swiped.current) setViewer(true); swiped.current = false; }}
            className={`w-full h-full flex-shrink-0 ${imgClassName} ${fullscreen ? "cursor-zoom-in" : ""}`} data-testid={`${testid}-img-${k}`} />
        ))}
      </div>
      {children}
      {badgeClass && <SampleBadge url={list[i]} className={`absolute z-10 ${badgeClass}`} testid={`${testid}-sample`} />}
      {cap && <div className={`absolute inset-x-0 ${overlayBottom ? `${overlayBottom} text-right pl-28 sm:pl-36` : "bottom-0"} z-10 bg-black/60 text-white text-xs sm:text-sm px-3 py-1.5 pb-5 truncate pointer-events-none`} data-testid={`${testid}-caption`}>{cap}</div>}
      {n > 1 && (
        <>
          <button type="button" onClick={() => go(-1)} aria-label="Previous photo" className={`${arrowCls} hidden sm:flex left-2 w-9 h-9 bg-black/45 hover:bg-black/65`} data-testid={`${testid}-prev`}><ChevronLeft className="w-5 h-5" /></button>
          <button type="button" onClick={() => go(1)} aria-label="Next photo" className={`${arrowCls} hidden sm:flex right-2 w-9 h-9 bg-black/45 hover:bg-black/65`} data-testid={`${testid}-next`}><ChevronRight className="w-5 h-5" /></button>
          <div className={`absolute ${overlayBottom ? `${overlayBottom} mb-1.5` : "bottom-1.5"} left-1/2 -translate-x-1/2 flex gap-1.5 z-10`} data-testid={`${testid}-dots`}>
            {list.map((_, k) => <button key={k} type="button" onClick={() => setI(k)} aria-label={`Photo ${k + 1}`} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} data-testid={`${testid}-dot-${k}`} />)}
          </div>
        </>
      )}
      {viewer && <FullscreenViewer list={list} captions={captions} start={i} alt={alt} onClose={closeViewer} testid={testid} />}
    </div>
  );
}
